/**
 * Globally-owned document-validation job. Survives minimise / navigation so
 * the run keeps going while the user browses elsewhere; ProcessingJobHost
 * renders it from MainLayout and ProcessingStatusBubble surfaces its state
 * on every page.
 */
import { create } from "zustand";
import {
  uploadGovernanceDocumentFile,
  subscribeDocEngineIngestEvents,
  getDomainClassifyResult,
  getConceptClassifyResult,
  getRequirementClassifyResult,
  getRelationshipClassifyResult,
  getConsistencyClassifyResult,
  getKnowledgeQualityClassifyResult,
  getGovernanceClassifyResult,
  saveDocumentIngest,
  deleteGovernanceDocumentOrphan,
  ApiEnvelopeError,
  DOC_INGEST_STATUS_DUPLICATE,
  type ConceptClassifyResult,
  type DocEngineDocumentRecord,
  type DocEngineIngestEvent,
  type DomainClassifyResult,
  type IngestStageEvent,
  type RequirementClassifyResult,
  type SaveDocumentIngestPayload,
  type ValidateFileResult,
} from "../app/pages/ContentRegistry/api/contentRegistryApi";
import { showToast } from "../app/components/ui/custom-toast";
import {
  JOB_PHASE,
  LAYER_RUN_STATUS,
  INGEST_STREAM_TIMEOUT_MS,
  DOC_INGEST_STATUS,
  type DocIngestStatus,
  type JobPhase,
  type LayerRunStatus,
} from "../app/pages/ContentRegistry/constants";

export { JOB_PHASE } from "../app/pages/ContentRegistry/constants";
export type { JobPhase } from "../app/pages/ContentRegistry/constants";

/**
 * Which flow started the run:
 *   create   - the create wizard (fresh upload + ingest)
 *   version  - upload a new version of an existing doc
 *   preview  - read-only re-open of an already-classified doc
 */
export type JobMode = "create" | "version" | "preview";

/** How a run ended, once there is nothing left on screen. */
export type JobOutcome = "uploaded" | "discarded" | "closed";

export interface JobError {
  title: string;
  message: string;
  code?: string;
}

/** Per-layer classification run state; `result` is loose so each layer can plug in its own shape. */
export interface LayerRunState {
  status: LayerRunStatus;
  note?: string;
  result?: unknown;
  error?: string;
}

/** Keys used in `ProcessingJob.layerRuns`. Aligned to `VALIDATION_LAYERS`. */
export type LayerKey =
  | "domain"
  | "requirement"
  | "concept"
  | "relationship"
  | "consistency"
  | "quality"
  | "governance";

export interface ProcessingJob {
  mode: JobMode;
  docId: string;
  file: File;
  documentTitle?: string;
  documentType?: string;
  domainName?: string;
  fileName?: string;
  fileSize?: string;
  /** Wizard metadata plumbed through so the save API can build its payload. */
  organization?: string;
  organizationId?: string;
  reviewCycle?: string;
  description?: string;
  phase: JobPhase;
  /** Reserved for a future aggregate response; the console reads per-layer state from `layerRuns`. */
  result: ValidateFileResult | null;
  error: JobError | null;
  apiDurationMs: number | null;
  consoleFinished: boolean;
  stage: number;
  /** Live per-stage state from the ingest SSE stream, keyed by backend stage name. */
  pipelineStageEvents: Record<string, IngestStageEvent>;
  pipelineProgress: number;
  /** Per-layer classification run state, keyed by `LayerKey`. */
  layerRuns: Partial<Record<LayerKey, LayerRunState>>;
  domainScopeKey?: string;
}

export interface StartJobParams {
  mode: JobMode;
  docId: string;
  file: File;
  documentTitle?: string;
  documentType?: string;
  domainName?: string;
  fileName?: string;
  fileSize?: string;
  organization?: string;
  organizationId?: string;
  reviewCycle?: string;
  description?: string;
  /** Create flow only: draft to delete if the run is abandoned. */
  orphanDocId?: string | null;
}

interface ProcessingJobStore {
  job: ProcessingJob | null;
  minimized: boolean;
  saving: boolean;
  orphanDocId: string | null;
  /** Bumped on every terminal outcome; pages refetch when it changes. */
  completionTick: number;
  lastOutcome: { mode: JobMode; kind: JobOutcome; orgId?: string } | null;
  /** Bumped when the starting flow should bring its file picker back. */
  reopenPickerTick: number;
  reopenTarget: { mode: JobMode; docId: string; error: JobError | null } | null;

  setOrphanDoc: (docId: string | null) => void;
  flushOrphanDoc: () => void;
  start: (params: StartJobParams) => Promise<void>;
  preview: (record: DocEngineDocumentRecord, scopeKey?: string) => Promise<void>;
  save: (status?: DocIngestStatus) => Promise<void>;
  cancel: () => Promise<void>;
  dismiss: () => Promise<void>;
  pickDifferent: () => void;
  markConsoleFinished: () => void;
  setStage: (stage: number) => void;
  minimize: () => void;
  restore: () => void;
  clear: () => void;
}

/* ── Module scope: survives every unmount ───────────────────────────────── */

let abortCtrl: AbortController | null = null;

/* ── Error mapping ──────────────────────────────────────────────────────── */

const ERROR_CODE_MESSAGES: Record<string, (detail: string) => string> = {
  "REG-2001-E": (d) =>
    `We couldn't read this PDF: ${d} Please pick a different file.`,
  "CR-0301-E": () =>
    "This document doesn't have an organization assigned yet. Set the organization on the document first, then try again.",
  "LLM-0005-E": (d) =>
    `${d} Configure an active LLM under LLM Configurations for this organization to enable validation.`,
  "LLM-0001-E": () =>
    "The active LLM rejected the request - its credentials may be invalid. Ask your administrator to check.",
  "LLM-0003-E": () =>
    "The active LLM model is not available. Ask your administrator to check the configuration.",
  "LLM-0004-E": () => "Couldn't reach the LLM provider. Try again in a moment.",
  "LLM-0099-E": () =>
    "Validation result couldn't be parsed. Try again, or pick a different model.",
  "SYS-0002-E": () => "Invalid file. Please pick a PDF.",
  "CR-0102-E": () => "This document is no longer editable.",
  "CR-0212-E": (d) => `File upload failed: ${d}`,
};

function mapError(e: unknown): JobError {
  if (e instanceof ApiEnvelopeError) {
    const mapper = ERROR_CODE_MESSAGES[e.code];
    return {
      title:
        e.code === "LLM-0005-E"
          ? "No active LLM for this document's organization"
          : e.title || "Validation failed",
      message: mapper ? mapper(e.detail) : e.detail,
      code: e.code,
    };
  }
  if (e instanceof TypeError && e.message === "Failed to fetch") {
    return {
      title: "No Response From Server",
      message:
        "The request was sent but the server never responded - this can happen when validation takes too long on a large document. Try again, try a smaller file, or check the server logs for this request if it keeps happening.",
    };
  }
  if (e instanceof Error) {
    return { title: e.name || "Error", message: e.message };
  }
  return { title: "Error", message: "Something went wrong." };
}

function isAbortError(e: unknown): boolean {
  return (
    (e instanceof DOMException && e.name === "AbortError") ||
    (e instanceof Error && e.name === "AbortError")
  );
}

/** Backend ingest-pipeline stage keys, in emit order. */
const INGEST_STAGE_KEYS = [
  "ingestion",
  "structure",
  "canonical_record",
  "semantic_chunking",
  "chunk_selection",
  "validate",
  "embed",
  "hub",
] as const;

const FAKE_COMPLETE_STAGE_EVENTS: Record<string, IngestStageEvent> =
  Object.fromEntries(
    INGEST_STAGE_KEYS.map((k) => [
      k,
      {
        stage: k,
        status: "done",
        finished: true,
        percentComplete: 100,
      } satisfies IngestStageEvent,
    ]),
  );

/* ── Ingest events (SSE) ────────────────────────────────────────────────── */

const TERMINAL_SUCCESS_STATUSES = new Set([
  "COMPLETED",
  "COMPLETE",
  "SUCCESS",
  "DONE",
  "FINISHED",
]);
const TERMINAL_FAILURE_STATUSES = new Set(["FAILED", "ERROR", "FAILURE"]);
const TERMINAL_SUCCESS_EVENTS = new Set([
  "completed",
  "complete",
  "done",
  "success",
  "finished",
]);
const TERMINAL_FAILURE_EVENTS = new Set(["failed", "error", "failure"]);

/** Whether an SSE frame terminates the whole run (see note on `stage` frames below). */
function isTerminalSuccess(name: string | undefined, status: string): boolean {
  const n = String(name ?? "").toLowerCase();
  if (n && TERMINAL_SUCCESS_EVENTS.has(n)) return true;
  // A `stage` frame's payload `status: "done"` is per-stage, not per-run.
  if (n === "stage") return false;
  return TERMINAL_SUCCESS_STATUSES.has(status);
}

function isTerminalFailure(name: string | undefined, status: string): boolean {
  const n = String(name ?? "").toLowerCase();
  if (n && TERMINAL_FAILURE_EVENTS.has(n)) return true;
  return TERMINAL_FAILURE_STATUSES.has(status);
}

/**
 * Subscribe to the ingest SSE stream and resolve when the pipeline signals
 * completion. Rejects with the backend's message on a FAILED event.
 *
 * `onEvent` fires for every non-terminal frame so the console can reflect
 * the current pipeline stage (chunking / embedding / …). A 10-minute safety
 * timeout closes the subscription so a wedged pipeline can't hang the run.
 */
async function waitForIngestEventsComplete(
  docId: string,
  signal: AbortSignal,
  onEvent: (name: string | undefined, evt: DocEngineIngestEvent) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      fn();
    };

    const timeoutId = setTimeout(() => {
      settle(() =>
        reject(
          new Error(
            "Document processing did not complete within 10 minutes. Try again or contact support.",
          ),
        ),
      );
    }, INGEST_STREAM_TIMEOUT_MS);

    subscribeDocEngineIngestEvents(docId, {
      signal,
      onEvent: (name, payload) => {
        if (settled) return;
        // Fire the caller for every frame so the per-stage map keeps up.
        onEvent(name, payload);

        const status = String(payload.status ?? "").toUpperCase();
        if (
          payload.failed === true ||
          isTerminalFailure(name, status) ||
          status === "FAILED" ||
          status === "ERROR"
        ) {
          settle(() =>
            reject(
              new Error(
                payload.error ||
                payload.message ||
                "Document processing failed on the server.",
              ),
            ),
          );
          return;
        }

        // `finished: true` on a `stage` frame is per-stage, so only accept
        // it as terminal when it rides on a `progress` frame.
        const nameLower = String(name ?? "").toLowerCase();
        const isProgressTerminal =
          nameLower === "progress" && payload.finished === true;
        if (isTerminalSuccess(name, status) || isProgressTerminal) {
          settle(resolve);
          return;
        }
      },
    })
      .then(() => settle(resolve))
      .catch((err) => {
        if (signal.aborted || isAbortError(err)) {
          settle(() => reject(new DOMException("Aborted", "AbortError")));
          return;
        }
        settle(() => reject(err));
      });
  });
}

/* ── Store ──────────────────────────────────────────────────────────────── */

export const useProcessingJobStore = create<ProcessingJobStore>((set, get) => {
  /** Best-effort DELETE of the draft this run created, if any. */
  const flushOrphan = () => {
    const id = get().orphanDocId;
    if (!id) return;
    set({ orphanDocId: null });
    deleteGovernanceDocumentOrphan(id);
  };

  /** Merge into the live job, ignoring late updates from a cleared run. */
  const patch = (patch: Partial<ProcessingJob>) => {
    const job = get().job;
    if (!job) return;
    set({ job: { ...job, ...patch } });
  };

  /** Merge into one entry of `layerRuns` without touching the rest. */
  const patchLayerRun = (key: LayerKey, delta: Partial<LayerRunState>) => {
    const job = get().job;
    if (!job) return;
    const current: LayerRunState = job.layerRuns[key] ?? {
      status: LAYER_RUN_STATUS.IDLE,
    };
    set({
      job: {
        ...job,
        layerRuns: { ...job.layerRuns, [key]: { ...current, ...delta } },
      },
    });
  };

  /**
   * Persist the ingested document with the given status before closing the
   * console so cancel/X and Save Document both reliably hit the lifecycle API.
   */
  const saveIngest = async (
    job: ProcessingJob,
    status: DocIngestStatus,
  ): Promise<boolean> => {
    const title =
      job.documentTitle?.trim() ||
      job.result?.document?.title?.trim() ||
      job.fileName?.replace(/\.[^/.]+$/, "").trim() ||
      "Untitled document";

    if (!job.docId) {
      showToast(
        "error",
        "Document could not be saved because the server did not return a document id.",
        "Save failed",
      );
      return false;
    }

    const payload: SaveDocumentIngestPayload = {
      documentId: job.docId,
      title,
      status,
    };
    if (job.documentType) {
      payload.documentType = job.documentType.toLowerCase();
    }
    if (job.domainName) payload.domainName = job.domainName;
    if (job.organization) payload.organization = job.organization;
    if (job.organizationId) payload.organizationId = job.organizationId;
    if (job.reviewCycle) payload.reviewCycle = job.reviewCycle;
    if (job.description) payload.description = job.description;

    try {
      await saveDocumentIngest(payload);
      return true;
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to save document.";
      showToast("error", msg, "Save failed");
      return false;
    }
  };

  /** Fire-and-forget domain classification; the result lands on `layerRuns.domain`. */
  /**
   * Fire-and-forget classifier runner used for every layer. Records
   * running/complete/error state on `layerRuns[layerKey]` and never rejects
   * the returned promise so callers can chain the next layer regardless of
   * this one's outcome.
   */
  const runLayerClassification = (
    layerKey: LayerKey,
    label: string,
    docId: string,
    fetcher: (id: string, signal: AbortSignal) => Promise<unknown>,
    signal: AbortSignal,
  ): Promise<void> => {
    if (!docId) {
      patchLayerRun(layerKey, {
        status: LAYER_RUN_STATUS.ERROR,
        error: `${label} classification could not run - missing document id.`,
      });
      return Promise.resolve();
    }
    patchLayerRun(layerKey, {
      status: LAYER_RUN_STATUS.RUNNING,
      note: undefined,
      error: undefined,
    });
    return fetcher(docId, signal)
      .then((result) => {
        if (signal.aborted) {
          patchLayerRun(layerKey, {
            status: LAYER_RUN_STATUS.IDLE,
            note: undefined,
            error: undefined,
          });
          return;
        }
        patchLayerRun(layerKey, {
          status: LAYER_RUN_STATUS.COMPLETE,
          result,
        });
      })
      .catch((err: unknown) => {
        if (signal.aborted) {
          // Aborted mid-flight — clear the RUNNING flag so the layer chip
          // does not spin forever after cancellation.
          patchLayerRun(layerKey, {
            status: LAYER_RUN_STATUS.IDLE,
            note: undefined,
            error: undefined,
          });
          return;
        }
        const message =
          err instanceof Error
            ? err.message
            : `${label} classification failed.`;
        patchLayerRun(layerKey, {
          status: LAYER_RUN_STATUS.ERROR,
          error: message,
        });
      });
  };

  /**
  * Chain the classifiers sequentially: Domain → Concept → Requirement →
  * Relationship → Consistency → Knowledge Quality → Governance. One call at
  * a time so the UI's progress cadence matches the seven-layer panel order.
   */
  const runDomainClassification = (docId: string, signal: AbortSignal) => {
    const layers: Array<{
      key: LayerKey;
      label: string;
      fetcher: (id: string, s: AbortSignal) => Promise<unknown>;
    }> = [
        {
          key: "domain",
          label: "Domain",
          fetcher: getDomainClassifyResult as (
            id: string,
            s: AbortSignal,
          ) => Promise<unknown>,
        },
        {
          key: "concept",
          label: "Concept",
          fetcher: getConceptClassifyResult as (
            id: string,
            s: AbortSignal,
          ) => Promise<unknown>,
        },
        {
          key: "requirement",
          label: "Requirement",
          fetcher: getRequirementClassifyResult as (
            id: string,
            s: AbortSignal,
          ) => Promise<unknown>,
        },
        {
          key: "relationship",
          label: "Relationship",
          fetcher: getRelationshipClassifyResult as (
            id: string,
            s: AbortSignal,
          ) => Promise<unknown>,
        },
        {
          key: "consistency",
          label: "Consistency",
          fetcher: getConsistencyClassifyResult as (
            id: string,
            s: AbortSignal,
          ) => Promise<unknown>,
        },
        {
          key: "quality",
          label: "Knowledge Quality",
          fetcher: getKnowledgeQualityClassifyResult as (
            id: string,
            s: AbortSignal,
          ) => Promise<unknown>,
        },
        {
          key: "governance",
          label: "Governance",
          fetcher: getGovernanceClassifyResult as (
            id: string,
            s: AbortSignal,
          ) => Promise<unknown>,
        },
      ];

    void layers.reduce<Promise<void>>(
      (prev, layer) =>
        prev.then(() => {
          if (signal.aborted) return;
          return runLayerClassification(
            layer.key,
            layer.label,
            docId,
            layer.fetcher,
            signal,
          );
        }),
      Promise.resolve(),
    );
  };

  const reset = () =>
    set({ job: null, minimized: false, saving: false, orphanDocId: null });

  /** Tell the flow that started the run to bring its file picker back. */
  const reopenPicker = (job: ProcessingJob, error: JobError | null) =>
    set({
      reopenTarget: { mode: job.mode, docId: job.docId, error },
      reopenPickerTick: get().reopenPickerTick + 1,
    });

  /** Tell the flow that started the run to refresh what it shows. */
  const signalCompletion = (
    mode: JobMode,
    kind: JobOutcome,
    orgId?: string,
  ) =>
    set({
      lastOutcome: { mode, kind, orgId },
      completionTick: get().completionTick + 1,
    });

  return {
    job: null,
    minimized: false,
    saving: false,
    orphanDocId: null,
    completionTick: 0,
    lastOutcome: null,
    reopenPickerTick: 0,
    reopenTarget: null,

    setOrphanDoc: (docId) => set({ orphanDocId: docId }),
    flushOrphanDoc: flushOrphan,

    start: async (params) => {
      abortCtrl?.abort();
      const ctrl = new AbortController();
      abortCtrl = ctrl;

      set({
        minimized: false,
        reopenTarget: null,
        // Keep whatever draft the caller registered before it got here.
        orphanDocId: params.orphanDocId ?? get().orphanDocId,
        job: {
          mode: params.mode,
          docId: params.docId,
          file: params.file,
          documentTitle: params.documentTitle,
          documentType: params.documentType,
          domainName: params.domainName,
          fileName: params.fileName ?? params.file.name,
          fileSize: params.fileSize,
          organization: params.organization,
          organizationId: params.organizationId,
          reviewCycle: params.reviewCycle,
          description: params.description,
          phase: JOB_PHASE.UPLOADING,
          result: null,
          error: null,
          apiDurationMs: null,
          consoleFinished: false,
          stage: 0,
          pipelineStageEvents: {},
          pipelineProgress: 0,
          layerRuns: {},
        },
      });

      const startedAt = Date.now();
      try {
        // Step 1: upload. Upload errors are handled inside so the user goes
        // back to the picker with an inline error instead of the validation
        // console showing progress for a run that never happened.
        let uploadedDocId = params.docId;
        try {
          const uploadRes = await uploadGovernanceDocumentFile(params.file, {
            orgId: params.organizationId,
            // Doc-engine domain name (always "SCRG") so the ingest
            // service stores it with the document — mirrors the save payload.
            domainName: params.domainName,
          });
          // Accept `{data: {document_id}}` or a flat `{document_id}` shape.
          const uploadPayload =
            uploadRes?.data ??
            (uploadRes as unknown as {
              document_id?: string;
              status?: string;
              message?: string;
            });
          const returnedId = uploadPayload?.document_id;
          const returnedStatus = String(uploadPayload?.status ?? "").toLowerCase();

          // Duplicate hit: the same file already exists for this org. Stop the
          // pipeline here so we don't kick off SSE / classification for the
          // existing document; bounce the user back to the picker to choose a
          // different file.
          if (returnedStatus === DOC_INGEST_STATUS_DUPLICATE) {
            const currentJob = get().job;
            const detail =
              typeof uploadPayload?.message === "string" && uploadPayload.message.trim()
                ? uploadPayload.message
                : "This file is already uploaded. Please choose a different document.";
            showToast("warning", detail, "Duplicate document");
            set({ job: null, minimized: false, orphanDocId: null });
            if (currentJob) reopenPicker(currentJob, null);
            return;
          }

          if (!returnedId) {
            throw new Error(
              "Upload succeeded but the server didn't return a document id.",
            );
          }
          uploadedDocId = returnedId;
          patch({ docId: returnedId });
        } catch (uploadErr: unknown) {
          if (ctrl.signal.aborted || isAbortError(uploadErr)) return;

          // CR-0303-E: backend upload rules rejected the file. Either the
          // draft got soft-deleted (first upload) or the prior file kept
          // (subsequent version) - either way, clear the orphan tracker.
          if (
            uploadErr instanceof ApiEnvelopeError &&
            uploadErr.code === "CR-0303-E"
          ) {
            const discarded = uploadErr.detail.includes("discarded");
            set({ orphanDocId: null });
            showToast(
              "error",
              discarded
                ? "Draft discarded. The file did not match this document's metadata or org rules. Re-create the document and upload a matching file."
                : "Upload rejected. The new file did not pass validation; the previously-uploaded file is retained.",
            );
            reset();
            signalCompletion(
              params.mode,
              discarded ? "discarded" : "uploaded",
            );
            return;
          }

          // Any other upload failure -> bounce back to the picker.
          const currentJob = get().job;
          const error = mapError(uploadErr);
          set({ job: null, minimized: false });
          if (currentJob) reopenPicker(currentJob, error);
          return;
        }
        if (ctrl.signal.aborted) return;
        set({ orphanDocId: null });
        patch({ phase: JOB_PHASE.RUNNING });

        // Step 2: ingest SSE. Populates the per-stage map; terminal FAILED
        // frames surface inline, and a 10-min safety timeout applies.
        try {
          await waitForIngestEventsComplete(
            uploadedDocId,
            ctrl.signal,
            (name, evt) => {
              const nameLower = String(name ?? "").toLowerCase();
              const currentJob = get().job;
              if (!currentJob) return;

              // A `stage` frame carries a single stage's terminal state.
              if (nameLower === "stage" && typeof evt.stage === "string") {
                const stageKey = evt.stage;
                const stageEntry: IngestStageEvent = {
                  stage: stageKey,
                  status: String(evt.status ?? ""),
                  ms: typeof evt.ms === "number" ? evt.ms : undefined,
                  detail:
                    typeof evt.detail === "string" ? evt.detail : undefined,
                  percentComplete:
                    typeof evt.percentComplete === "number"
                      ? evt.percentComplete
                      : undefined,
                  finished: evt.finished === true,
                  error:
                    typeof evt.error === "string" ? evt.error : undefined,
                };
                const nextEvents = {
                  ...currentJob.pipelineStageEvents,
                  [stageKey]: stageEntry,
                };
                set({
                  job: { ...currentJob, pipelineStageEvents: nextEvents },
                });
                return;
              }

              // A `progress` / `done` frame carries the overall snapshot;
              // absorb `percentComplete` and any embedded per-stage roll-up.
              if (nameLower === "progress" || nameLower === "done") {
                const nextEvents = { ...currentJob.pipelineStageEvents };
                if (Array.isArray(evt.stages)) {
                  for (const s of evt.stages) {
                    if (s && typeof s.stage === "string") {
                      nextEvents[s.stage] = { ...s };
                    }
                  }
                }
                set({
                  job: {
                    ...currentJob,
                    pipelineStageEvents: nextEvents,
                    pipelineProgress:
                      typeof evt.percentComplete === "number"
                        ? Math.max(
                          0,
                          Math.min(100, Math.round(evt.percentComplete)),
                        )
                        : currentJob.pipelineProgress,
                  },
                });
              }
            },
          );
        } catch (extractErr: unknown) {
          if (ctrl.signal.aborted || isAbortError(extractErr)) return;
          patch({ phase: JOB_PHASE.ERROR, error: mapError(extractErr) });
          return;
        }
        if (ctrl.signal.aborted) return;

        // Hub done - kick off per-layer classifiers in the background and
        // settle. Layer runs populate `layerRuns.<key>` off the critical path.
        patch({
          phase: JOB_PHASE.VALIDATING_LAYERS,
          pipelineProgress: 100,
        });
        runDomainClassification(uploadedDocId, ctrl.signal);
        patch({
          apiDurationMs: Date.now() - startedAt,
          phase: JOB_PHASE.COMPLETE,
        });
      } catch (err: unknown) {
        if (ctrl.signal.aborted || isAbortError(err)) return;
        patch({ phase: JOB_PHASE.ERROR, error: mapError(err) });
      } finally {
        if (abortCtrl === ctrl) abortCtrl = null;
      }
    },

    preview: async (record, scopeKey) => {
      // Guard: record must carry a documentId - the classify GET is keyed
      // by it, and an empty id would build a broken URL.
      if (!record?.documentId) {
        showToast(
          "error",
          "This document is missing an id and can't be previewed.",
          "Preview failed",
        );
        return;
      }

      // No-op: the same doc is already being previewed. Prevents duplicate
      // fetches from double-clicks or repeated Preview presses.
      const existing = get().job;
      if (
        existing?.mode === "preview" &&
        existing.docId === record.documentId
      ) {
        const nextScope = scopeKey?.trim() || undefined;
        if (existing.domainScopeKey !== nextScope) {
          set({ minimized: false, job: { ...existing, domainScopeKey: nextScope } });
        } else {
          set({ minimized: false });
        }
        return;
      }

      abortCtrl?.abort();
      const ctrl = new AbortController();
      abortCtrl = ctrl;

      // Empty placeholder file - preview never actually POSTs a multipart
      // request, but ProcessingJob keeps `file` as required for live runs.
      const placeholderFile = new File([], record.filename ?? "document");

      set({
        minimized: false,
        reopenTarget: null,
        orphanDocId: null,
        job: {
          mode: "preview",
          docId: record.documentId,
          file: placeholderFile,
          documentTitle: record.title,
          documentType: record.documentType,
          fileName: record.filename,
          fileSize: undefined,
          organization: record.organization,
          organizationId: record.organizationId,
          reviewCycle: record.reviewCycle,
          description: record.description,
          phase: JOB_PHASE.COMPLETE,
          result: null,
          error: null,
          apiDurationMs: 0,
          // Skip the closing animation - the pipeline strip is already
          // fully green (all stages pre-marked done).
          consoleFinished: true,
          stage: INGEST_STAGE_KEYS.length - 1,
          pipelineStageEvents: { ...FAKE_COMPLETE_STAGE_EVENTS },
          pipelineProgress: 100,
          layerRuns: {},
          domainScopeKey: scopeKey?.trim() || undefined,
        },
      });

      // Kick off the same Domain -> Concept -> Requirement chain the live
      // ingest flow uses, so preview fetches every classifier the user
      // might click on in the seven-layer panel. Runs fire-and-forget;
      // abortCtrl stays set so a subsequent preview / start can cancel it.
      runDomainClassification(record.documentId, ctrl.signal);
    },

    save: async (status = DOC_INGEST_STATUS.APPROVED) => {
      const job = get().job;
      if (!job || get().saving) return;
      // Preview is a read-only re-open; there's nothing new to persist.
      if (job.mode === "preview") {
        reset();
        return;
      }
      const orgId = job.organizationId;
      set({ saving: true });
      const saved = await saveIngest(job, status);
      set({ saving: false });
      if (!saved) return;
      const isDraft = status === DOC_INGEST_STATUS.DRAFT;
      showToast(
        "success",
        isDraft
          ? "Document saved as draft. You can continue reviewing it later."
          : job.mode === "create"
            ? "Document approved and saved"
            : "File approved and saved.",
      );
      reset();
      signalCompletion(job.mode, isDraft ? "closed" : "uploaded", orgId);
    },

    cancel: async () => {
      if (get().saving) return;
      const job = get().job;
      // Preview is read-only: just close, no auto-save, no re-open picker.
      if (job?.mode === "preview") {
        abortCtrl?.abort();
        abortCtrl = null;
        reset();
        return;
      }

      if (job && job.phase !== JOB_PHASE.ERROR) {
        const orgId = job.organizationId;
        set({ saving: true });
        const saved = await saveIngest(job, DOC_INGEST_STATUS.DRAFT);
        set({ saving: false });
        if (!saved) return;
        showToast(
          "success",
          "Document saved as draft. You can continue reviewing it later.",
          "Saved",
        );
        abortCtrl?.abort();
        abortCtrl = null;
        reset();
        signalCompletion(job.mode, "closed", orgId);
      } else {
        flushOrphan();
        abortCtrl?.abort();
        abortCtrl = null;
        reset();
      }
      if (job) {
        reopenPicker(job, job.phase === JOB_PHASE.ERROR ? job.error : null);
      }
    },

    dismiss: async () => {
      if (get().saving) return;
      const job = get().job;
      if (!job) return;
      // Preview is read-only: just close.
      if (job.mode === "preview") {
        abortCtrl?.abort();
        abortCtrl = null;
        reset();
        return;
      }
      if (job.phase === JOB_PHASE.ERROR) {
        abortCtrl?.abort();
        abortCtrl = null;
        flushOrphan();
        reset();
        reopenPicker(job, job.error);
        return;
      }
      const orgId = job.organizationId;
      set({ saving: true });
      const saved = await saveIngest(job, DOC_INGEST_STATUS.DRAFT);
      set({ saving: false });
      if (!saved) return;
      showToast(
        "success",
        "Document saved as Draft. You can continue reviewing it later.",
        "Saved",
      );
      abortCtrl?.abort();
      abortCtrl = null;
      reset();
      signalCompletion(job.mode, "closed", orgId);
    },

    pickDifferent: () => {
      abortCtrl?.abort();
      abortCtrl = null;
      const job = get().job;
      if (!job) return;
      // Keep the draft so re-validation reuses the same document.
      set({ job: null, minimized: false });
      reopenPicker(job, null);
    },

    markConsoleFinished: () => patch({ consoleFinished: true }),

    setStage: (stage) => {
      const job = get().job;
      if (!job || job.stage === stage) return;
      set({ job: { ...job, stage } });
    },

    minimize: () => set({ minimized: true }),
    restore: () => set({ minimized: false }),

    clear: () => {
      abortCtrl?.abort();
      abortCtrl = null;
      reset();
    },
  };
});

// Safety net: flush orphan drafts on tab close (route changes deliberately don't).
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    useProcessingJobStore.getState().flushOrphanDoc();
  });
}
